import React, { useState, useRef, useEffect } from "react";
import { gsap } from "gsap";
import { useTranslation } from "react-i18next";
import { Button, Input, Alert } from "@repo/ui";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";
import {
  signUpWithEmail,
  signInWithEmail,
  signInWithGoogle,
  getIdToken,
} from "../../services/firebaseService";
import { authenticateWithBackend } from "../../services/authApiService";
import { login } from "../../redux/slices/authSlice";
import { getActiveGiftTableByUserID } from "../../services/giftTableApiService";
import { fetchAndLoadGiftTable } from "../../redux/slices/giftTableSlice";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { User } from "firebase/auth";
import { AxiosError } from "axios";
import { withTranslations } from "../../hoc/withTranslations/withTranslations";
import { resetClientStorage } from "../../utils/resetClientStorage";
import { resetApp } from "../../redux/store";

const LOCALE = "components_AuthMethods_AuthMethods";

type Level = "info" | "warn" | "error" | "success";
interface AuthMethodsProps {
  mode: "signup" | "signin";
}

const AuthMethods: React.FC<AuthMethodsProps> = ({ mode }) => {
  const { t } = useTranslation(LOCALE);
  const [, setCookie] = useCookies(["authToken"]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginFailed, setLoginFailed] = useState(false);
  const [alert, setAlert] = useState<{
    message: React.ReactNode;
    level: Level;
  } | null>(null);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const alertRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (alertRef.current) {
      gsap.fromTo(
        alertRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 0.5 }
      );
    }
  }, [alert]);

  const getAlertMessages = (): Record<
    string,
    { message: string | React.ReactNode; level: string }
  > => ({
    "auth/email-already-in-use": {
      message: (
        <span>
          {t("email-already-in-use")}{" "}
          <a href="/registration/signin" className="bold">
            {t("signin-link")}
          </a>
        </span>
      ),
      level: "warn",
    },
    "auth/invalid-email": {
      message: t("invalid-email"),
      level: "error",
    },
    "auth/weak-password": {
      message: t("weak-password"),
      level: "error",
    },
    "signup/success": {
      message: t("signup-success"),
      level: "success",
    },
    "login/success": {
      message: t("login-success"),
      level: "success",
    },
    "login/google-error": {
      message: t("google-error"),
      level: "error",
    },
    "login/facebook-error": {
      message: t("facebook-error"),
      level: "error",
    },
    "error.auth-failed": {
      message: t("error.auth-failed"),
      level: "error",
    },
    "error.generic": {
      message: t("error.generic"),
      level: "error",
    },
  });

  const handleSuccessfulLogin = async (user: User) => {
    try {
      const firebaseToken = await getIdToken();
      const authApiResponse = await authenticateWithBackend(firebaseToken);

      await resetClientStorage({ preserveLocalStorageKeys: ["language"] });
      dispatch(resetApp());

      setCookie("authToken", authApiResponse.token, {
        path: "/",
        secure: true,
        sameSite: "strict",
      });

      setAlert({
        message: getAlertMessages()["login/success"].message,
        level: getAlertMessages()["login/success"].level as
          | "success"
          | "error"
          | "info"
          | "warn",
      });

      dispatch(
        login({
          name: authApiResponse.user.name || (user.displayName as string),
          photoUrl: authApiResponse.user.avatar || (user.photoURL as string),
          email: email || (user.email as string),
          id: authApiResponse.user.id,
          acceptsTerms: authApiResponse.user.accepts_terms,
          acceptsPrivacy: authApiResponse.user.accepts_privacy,
        })
      );

      const activeTableID = await getActiveGiftTableByUserID(
        authApiResponse.user.id
      );
      if (activeTableID) {
        dispatch(fetchAndLoadGiftTable(activeTableID));
        navigate("/my-table");
      } else {
        navigate("/registration/accept-terms");
      }
    } catch (error) {
      setAlert({
        message: getAlertMessages()["error.auth-failed"].message,
        level: getAlertMessages()["error.auth-failed"].level as "error",
      });
    }
  };

  const handleEmailAuth = async () => {
    try {
      const user =
        mode === "signup"
          ? await signUpWithEmail(email, password)
          : await signInWithEmail(email, password);
      await handleSuccessfulLogin(user);
      setLoginFailed(false);
    } catch (error) {
      if (mode === "signin") {
        setLoginFailed(true);
      }
      const errorMessage =
        getAlertMessages()[(error as AxiosError).code as string] ||
        getAlertMessages()["error.generic"];
      setAlert(errorMessage as typeof alert);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const user = await signInWithGoogle();
      await handleSuccessfulLogin(user);
    } catch (error) {
      setAlert({
        message: getAlertMessages()["login/google-error"].message,
        level: "error",
      });
    }
  };

  return (
    <div className="flex flex-col space-y-4">
      {alert && (
        <div ref={alertRef}>
          <Alert message={alert.message} level={alert.level} />
        </div>
      )}
      <div className="flex flex-col space-y-2">
        <label htmlFor="email" className="text-label font-paragraph">
          {t("signup.email-label")}
        </label>
        <Input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          type="email"
          className="input-border"
        />
        <label htmlFor="password" className="text-label font-paragraph">
          {t("signup.password-label")}
        </label>
        <Input
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          type="password"
          className="input-border"
        />
        {mode === "signin" && loginFailed && (
          <div className="text-center text-red-500 mt-2">
            <a href="/registration/password-recovery" className="underline">
              {t("signin.forgot-password")}
            </a>
          </div>
        )}

        <Button
          children={t(
            mode === "signup"
              ? "signup.continue-button"
              : "signin.continue-button"
          )}
          onClick={handleEmailAuth}
          type="primary"
        />
      </div>
      <div className="flex justify-center items-center my-4">
        <span className="border-t border-gray-300 w-full"></span>
        <span className="px-2 text-gray-600 font-paragraph text-small text-center">
          {t(
            mode === "signup"
              ? "signup.alternative-text"
              : "signin.alternative-text"
          )}
        </span>
        <span className="border-t border-gray-300 w-full"></span>
      </div>
      <Button
        children={
          <span className="btn-icon">
            <img src="/icons/google.svg" alt="Google" />
            <span>
              {t(
                mode === "signup"
                  ? "signup.google-button"
                  : "signin.google-button"
              )}
            </span>
          </span>
        }
        onClick={handleGoogleLogin}
        type="secondary"
      />
    </div>
  );
};

export default withTranslations(LOCALE)(AuthMethods);
